import { useState, useEffect } from "react";
import { X, Calendar, Clock, CheckCircle2, ArrowLeft, ArrowRight, Phone, Mail, User, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, SERVICES } from "@/lib/api";

const TIME_SLOTS = [
  "9:00 AM ET", "10:00 AM ET", "11:00 AM ET",
  "1:00 PM ET", "2:00 PM ET", "3:00 PM ET", "4:00 PM ET",
];

// Build next 14 weekdays
function getAvailableDates() {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < 14) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      out.push(new Date(d));
    }
  }
  return out;
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}
function fmtLabel(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function BookCallModal({ open, onClose, defaultVenture = "" }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", venture: defaultVenture, notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const dates = getAvailableDates();

  useEffect(() => {
    if (open) {
      setStep(1);
      setDate(null);
      setSlot("");
      setDone(false);
      setForm((f) => ({ ...f, venture: defaultVenture || "Business Consulting" }));
    }
  }, [open, defaultVenture]);

  useEffect(() => {
    const loadAvail = async () => {
      if (!date) return;
      try {
        const { data } = await api.get(`/bookings/availability?date=${fmtDate(date)}`);
        setTaken(data.taken || []);
      } catch {
        setTaken([]);
      }
    };
    loadAvail();
  }, [date]);

  // Lock body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        venture: form.venture,
        date: fmtDate(date),
        time_slot: slot,
        notes: form.notes,
      });
      setDone(true);
      toast.success("Confirmed. A reminder will arrive by email.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed. Try another slot.";
      toast.error(msg);
      if (err?.response?.status === 409) setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="book-call-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-full max-w-2xl my-8 relative overflow-hidden animate-fade-up"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-lime to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-lime/10 border border-neon-lime/40 flex items-center justify-center">
              <Sparkles size={18} className="text-neon-lime" />
            </div>
            <div>
              <div className="overline">// 15-MIN CONVERSATION</div>
              <div className="font-heading font-bold text-lg mt-0.5">Complimentary · Unhurried</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="book-call-close"
            className="w-9 h-9 flex items-center justify-center text-white/50 hover:text-white border border-white/10 hover:border-white/40 transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Stepper */}
        {!done && (
          <div className="px-6 py-3 border-b border-white/10 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
            {["Pick date", "Pick time", "Your info"].map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const completed = step > n;
              return (
                <div key={label} className="flex items-center gap-2">
                  <span className={`w-5 h-5 flex items-center justify-center border ${active ? "border-neon-lime text-neon-lime" : completed ? "border-neon-lime/40 text-neon-lime/60" : "border-white/20 text-white/30"}`}>
                    {n}
                  </span>
                  <span className={active ? "text-white" : "text-white/40"}>{label}</span>
                  {i < 2 && <span className="text-white/20 mx-1">›</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
          {done ? (
            <div data-testid="book-call-success" className="text-center py-10">
              <CheckCircle2 size={56} className="text-neon-lime mx-auto mb-6" />
              <h3 className="font-heading font-black text-3xl mb-3">You&apos;re on the calendar.</h3>
              <p className="text-white/60 mb-2">
                {fmtLabel(date)} · <span className="text-neon-lime">{slot}</span>
              </p>
              <p className="text-white/50 text-sm max-w-md mx-auto">
                A confirmation is on its way to <span className="text-white">{form.email}</span>. We&apos;ll follow up with a calendar invite and meeting link shortly.
              </p>
              <button onClick={onClose} className="btn-primary mt-8">Done</button>
            </div>
          ) : step === 1 ? (
            <div data-testid="book-step-date">
              <h3 className="font-heading font-bold text-xl mb-1">Choose a day</h3>
              <p className="text-white/50 text-sm mb-6">Weekdays only. All times Eastern.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
                {dates.map((d) => {
                  const selected = date && fmtDate(d) === fmtDate(date);
                  return (
                    <button
                      key={fmtDate(d)}
                      onClick={() => setDate(d)}
                      data-testid={`book-date-${fmtDate(d)}`}
                      className={`p-3 border text-center transition-all ${
                        selected
                          ? "border-neon-lime bg-neon-lime/5 text-neon-lime"
                          : "border-white/10 hover:border-white/30 text-white/70"
                      }`}
                    >
                      <div className="font-mono text-[9px] uppercase tracking-widest opacity-60">
                        {d.toLocaleDateString("en-US", { weekday: "short" })}
                      </div>
                      <div className="font-heading font-bold text-xl mt-1">{d.getDate()}</div>
                      <div className="font-mono text-[9px] uppercase opacity-60">
                        {d.toLocaleDateString("en-US", { month: "short" })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : step === 2 ? (
            <div data-testid="book-step-time">
              <h3 className="font-heading font-bold text-xl mb-1">Choose a time</h3>
              <p className="text-white/50 text-sm mb-6 flex items-center gap-2">
                <Calendar size={13} /> {fmtLabel(date)} · Eastern Time
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TIME_SLOTS.map((t) => {
                  const isTaken = taken.includes(t);
                  const selected = slot === t;
                  return (
                    <button
                      key={t}
                      onClick={() => !isTaken && setSlot(t)}
                      disabled={isTaken}
                      data-testid={`book-slot-${t}`}
                      className={`p-3 border font-mono text-xs uppercase tracking-wider transition-all ${
                        isTaken
                          ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                          : selected
                          ? "border-neon-lime bg-neon-lime/5 text-neon-lime"
                          : "border-white/10 hover:border-white/30 text-white/70"
                      }`}
                    >
                      <Clock size={11} className="inline mr-1.5" />
                      {t}
                    </button>
                  );
                })}
              </div>
              {taken.length > 0 && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-4">
                  // {taken.length} slot{taken.length > 1 ? "s" : ""} already booked
                </p>
              )}
            </div>
          ) : (
            <div data-testid="book-step-info" className="space-y-4">
              <div className="glass p-4 border-l-2 border-neon-lime mb-2">
                <div className="overline">// Confirming</div>
                <div className="font-heading font-bold text-base mt-1">
                  {fmtLabel(date)} <span className="text-neon-lime">· {slot}</span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-2 block">// Name *</label>
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="book-name"
                      className="tsv-input !pl-10"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="overline mb-2 block">// Email *</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="book-email"
                      className="tsv-input !pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-2 block">// Phone</label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="book-phone"
                      className="tsv-input !pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline mb-2 block">// Focus *</label>
                  <select
                    value={form.venture}
                    onChange={(e) => setForm({ ...form, venture: e.target.value })}
                    data-testid="book-venture"
                    className="tsv-input cursor-pointer appearance-none"
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name} className="bg-[#0F0F16]">{s.name}</option>
                    ))}
                    <option value="Not Sure Yet" className="bg-[#0F0F16]">Not Sure Yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="overline mb-2 block">// Anything we should know?</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="book-notes"
                  className="tsv-input resize-none"
                  placeholder="A sentence or two about what you'd like to walk away with…"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3 bg-black/30">
            <button
              onClick={() => step > 1 && setStep(step - 1)}
              disabled={step === 1}
              data-testid="book-back"
              className="btn-ghost !py-2.5 !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 hidden sm:block">
              Step {step} of 3
            </div>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !date) || (step === 2 && !slot)}
                data-testid="book-next"
                className="btn-primary !py-2.5 !px-5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={loading}
                data-testid="book-submit"
                className="btn-primary !py-2.5 !px-5 disabled:opacity-40"
              >
                {loading ? "Confirming…" : "Confirm"} <CheckCircle2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
